import { Injectable } from '@angular/core';
import { DMS } from '../../models/dms';
import { Direction } from '../../models/direction';
import { Parallel } from '../../models/parallel';

@Injectable({
    providedIn: 'root',
})
export class GeoCoordinateSystemService {
    /*
     * Rounds a number to 6 decimal places, the identified precision for EWA.
     */
    roundToPrecision(value: number): number {
        return parseFloat(value.toFixed(6));
    }

    /*
     * Converts a DMS (Degrees/Minutes/Seconds) coordinate to a decimal coordinate. The decimal coordinate is rounded to 6 decimal places.
     */
    convertDMSToDecimal(coordinate: DMS): number {
        const degrees = coordinate.degrees || 0;
        const minutes = coordinate.minutes || 0;
        const seconds = coordinate.seconds || 0;

        let value = degrees + minutes / 60 + seconds / 3600;

        if (
            coordinate.direction === Direction.South ||
            coordinate.direction === Direction.West
        ) {
            value *= -1;
        }

        return this.roundToPrecision(value);
    }

    /*
     * Converts a decimal coordinate to a DMS (Degrees/Minutes/Seconds) coordinate.
     */
    convertDecimalToDMS(decimal: number, type: Parallel): DMS {
        decimal = this.roundToPrecision(decimal);

        const direction =
            decimal < 0
                ? type === 'latitude'
                    ? Direction.South
                    : Direction.West
                : type === 'latitude'
                  ? Direction.North
                  : Direction.East;

        const degrees = Math.floor(Math.abs(decimal));
        const minutes = Math.floor((Math.abs(decimal) - degrees) * 60);

        // Allow seconds to be rounded to the nearest tenth
        const seconds = parseFloat(
            ((Math.abs(decimal) - degrees - minutes / 60) * 3600).toFixed(1),
        );

        return {
            degrees,
            minutes,
            seconds,
            direction,
        };
    }

    /**
     * Converts a decimal value to a formatted DMS string.
     * Example: 37.7749 -> 37° 46' 29.6" N
     * @param decimal the decimal value to convert to DMS
     * @param type the type of parallel to convert
     * @returns
     */
    getFormattedDMS(
        decimal: number | null,
        type: Parallel,
    ): string | undefined {
        if (!decimal) {
            return undefined;
        }

        const dms = this.convertDecimalToDMS(decimal, type);

        return `${dms.degrees}° ${dms.minutes}' ${dms.seconds}" ${dms.direction}`;
    }
}
